/* eslint-disable react-refresh/only-export-components */
import { useContext, createContext, useState, useRef } from "react";
import { clearUserState, JWT_TOKEN, setLoggedInState } from "./auth";
import { checkSession } from "./session";
import { decodeJWT } from "@/utils/encoders";
import { LOGIN_ROUTE } from "@/routing/app-routes";
import { usePostHog } from "posthog-js/react";
import {
  posthogIdentifyUser,
  posthogIdentifyWorkspace,
  posthogReset,
} from "@/utils/posthog";
import useGetAuthUser from "@/api/routes/users/get-auth-user";
import { PROVIDERS } from "@/components/pages/login-page/login-constants";

const AuthContext = createContext();
const AuthProvider = ({ children }) => {
  const posthog = usePostHog();
  const [token, setToken] = useState(
    () => localStorage.getItem(JWT_TOKEN) || null
  );
  const [user, setUser] = useState(null);
  const [defaultWorkspaceId, setDefaultWorkspaceId] = useState();
  const fetchAuthUser = useGetAuthUser(false);
  const checkedForUser = useRef(false);

  const setLoginAuth = async (user) => {
    posthogIdentifyUser(posthog, user);
    posthogIdentifyWorkspace(posthog, user);
    setLoggedInState(user.jwt);
    return saveUserState(user);
  };

  const saveUserState = (user) => {
    const workspaceId = user?.Workspaces?.[0]?.id;
    setDefaultWorkspaceId(workspaceId);
    setUser(user);
    if (user.jwt) {
      setToken(user.jwt);
    }
    return workspaceId;
  };

  const logoutAuth = async (redirectUser = true) => {
    try {
      clearUserState();
      setToken(null);
      posthogReset(posthog);
      // cant use useNavigate here since this wraps the Router
      if (redirectUser) {
        window.location.href = LOGIN_ROUTE;
      }
    } catch (e) {
      console.error(e);
    }
  };

  const checkUserAuthSession = () => {
    const getUser = async () => {
      try {
        const { exp } = token ? decodeJWT(token) : { exp: 0 };
        if (checkSession(exp)) {
          setLoggedInState(token);
          await fetchAuthUser.refetch();
        }
      } catch {
        logoutAuth(false);
      }
    };

    if (token && !user && !checkedForUser.current) {
      checkedForUser.current = true;
      getUser();
    }

    if (!user && fetchAuthUser.isSuccess) {
      const fetchedUser = fetchAuthUser.data;
      if (fetchedUser.id) {
        posthogIdentifyUser(posthog, fetchedUser);
        posthogIdentifyWorkspace(posthog, fetchedUser);
        saveUserState(fetchedUser);
      }
    }
  };
  const isOauthUser = user?.provider !== PROVIDERS.NONE.name;

  checkUserAuthSession();
  return (
    <AuthContext
      value={{
        user,
        defaultWorkspaceId,
        token,
        isOauthUser,
        setLoginAuth,
        logoutAuth,
        setUser,
      }}
    >
      {children}
    </AuthContext>
  );
};
export default AuthProvider;
export const useAuth = () => {
  return useContext(AuthContext);
};
